import { PromiseExtended, Table } from 'dexie';
import { AnalyticsEvent } from '../types/analytics';
import { SyncChanges, SyncRecord, SyncRecordStatus } from '../types/sync-state';
import { ITeacherProfile } from '../types/user';
import { userCache } from '../utils/user-cache';
import { BaseModelForPush } from './base-model';
import { syncedDBModel } from './synced';

const SCHEMA_VERSION = 1;
const SCHEMA = {
  events: '++id, user, profile, syncStatus',
};

export interface AnalyticsEventRecord extends AnalyticsEvent, SyncRecord {}

export class AnalyticsDB extends BaseModelForPush {
  events!: Table<AnalyticsEventRecord>;

  constructor() {
    super('analtyicsdb');
    this.version(SCHEMA_VERSION).stores(SCHEMA);

    this.setupTableHooks([this.events]);
  }

  /**
   * ! Do not use this method directly. Use `AnalyticsService` instead.
   */
  async trackEvent(event: AnalyticsEvent) {
    // TODO what is a better way to manage this? Should we put this
    // as part of the update hook in the base model?
    // TODO we should wrap this in a transaction?
    if (process.env.NX_PUBLIC_ENV !== 'production') console.log(event);
    syncedDBModel.updateChangedAt();
    return this.events.add(event as AnalyticsEventRecord);
  }

  async getNewEventRecord(): Promise<PromiseExtended<SyncRecord[]>> {
    const selectedProfile = userCache.getActiveProfile() as ITeacherProfile;
    const data = this.events
      .where('syncStatus')
      .equals(SyncRecordStatus.Created.toString())
      .toArray();
    const updatedRecord = (await data).map((record) => ({
      ...record,
      // todo: handle the case when for all profiles
      // school: selectedProfile?.school,
    }));
    return updatedRecord;
  }

  async getAllUnsyncedRecords(): Promise<Record<string, SyncRecord[]>> {
    const records = {
      analyticEvents: await this.getNewEventRecord(),
    };
    return records;
  }

  async updateRecordsSyncStatus(
    syncChanges: SyncChanges,
    status: SyncRecordStatus,
  ): Promise<number> {
    const ids = syncChanges.analyticEvents?.created.map(
      (record: { id: number }) => record.id,
    );
    return this._updateSyncData(this.events, ids, status);
  }

  /**
   * MC20-18126: 30-day retention for the analytics events table.
   *
   * The events table has no built-in TTL, so on a long-lived install it
   * accumulates ~1 MB/month of cruft (12+ MB/year) and contributes to
   * IndexedDB quota pressure on low-end Android. This method deletes events
   * older than `olderThanMs` AND already synced — never deletes unsynced
   * events that still need to be uploaded.
   *
   * Returns the number of rows deleted.
   */
  async pruneOldEvents(
    olderThanMs: number = 30 * 24 * 60 * 60 * 1000,
    now: () => Date = () => new Date(),
  ): Promise<number> {
    const cutoff = now().getTime() - olderThanMs;
    // Scan the whole table once on startup. The table is small (low MB) and
    // `sentAt` is not indexed, so a `.filter()` is fine here. We only collect
    // IDs (not whole rows) to keep the working set tiny during the scan.
    const idsToDelete: number[] = [];
    await this.events.each((record) => {
      // Never prune an unsynced event — it still needs to be uploaded.
      if (record.syncStatus !== SyncRecordStatus.Synced) return;
      const sentAt = record.sentAt;
      const sentAtMs =
        sentAt instanceof Date
          ? sentAt.getTime()
          : new Date(sentAt as unknown as string).getTime();
      if (Number.isFinite(sentAtMs) && sentAtMs < cutoff) {
        // `id` is auto-incremented by Dexie (++id in the schema). It is not
        // declared on the AnalyticsEventRecord interface, so we read it via
        // an `unknown` cast.
        const id = (record as unknown as { id?: number }).id;
        if (id != null) idsToDelete.push(id);
      }
    });
    if (idsToDelete.length === 0) return 0;
    await this.events.bulkDelete(idsToDelete);
    return idsToDelete.length;
  }
}

export const analyticsDB = new AnalyticsDB();
